import { useEffect, useRef, useState } from 'react'
import { asRichHtml, isRichTextEmpty, sanitizeRichText } from '@/lib/richText'
import { EditorToolbar } from './editortoolbar'
import { cx } from './primitives'

/**
 * Writing that will print on a quotation, edited as it will read.
 *
 * <p>
 * The same bar as the email editor, and every control on it is one the document draws: a
 * toolbar button whose formatting the PDF discards is a promise the quotation breaks. What
 * keeps that true is not this component but the two lists either side of it -
 * <code>sanitizeRichText</code> decides what is stored, RichText.ToBlocks and
 * QuotationDocument.Prose in the API decide what is drawn, and they are the same list.
 * </p>
 *
 * <p>
 * Built on <code>contenteditable</code> and <code>document.execCommand</code> rather than an
 * editor library. Every browser still implements these commands, and the alternative is a
 * dependency measured in hundreds of kilobytes to produce the same handful of tags.
 * <code>execCommand</code> is deprecated and has no replacement for exactly this - the note
 * has stood for years - so the trade is accepted and written down rather than discovered
 * later.
 * </p>
 *
 * The box is uncontrolled, like every other text field in this application: React does not
 * own the caret. The stored value is written in on mount and again when it changes from
 * outside while nobody is typing, because a save that answers with the stored text must not
 * move the cursor of somebody who has carried on writing.
 */
export function RichTextEditor({
  id,
  value,
  disabled,
  placeholder,
  minHeight = '5.5rem',
  onChange,
  onBlur,
}: {
  id?: string
  /** Stored HTML. Plain text from before this editor existed is given its paragraphs back. */
  value: string | null | undefined
  disabled?: boolean
  placeholder?: string
  minHeight?: string
  onChange?: (html: string) => void
  /** The sanitised HTML, once. Compare it against what you loaded before saving. */
  onBlur?: (html: string) => void
}) {
  const host = useRef<HTMLDivElement>(null)
  const [empty, setEmpty] = useState(() => isRichTextEmpty(value))

  // What is on screen, so an echo of our own edit is not written back over the caret.
  const shown = useRef('')

  useEffect(() => {
    const element = host.current
    if (!element) return

    // Sanitised on the way in as well as out: this assigns innerHTML, and what is stored
    // was not necessarily written by this editor.
    const incoming = sanitizeRichText(asRichHtml(value))
    if (incoming === shown.current) return

    // Somebody typing owns the box until they leave it. Everything else - the first load,
    // a block handed the product's narrative, a discarded edit - is written in.
    if (document.activeElement === element) return

    element.innerHTML = incoming
    shown.current = incoming
    setEmpty(isRichTextEmpty(incoming))
  }, [value])

  function read() {
    const html = sanitizeRichText(host.current?.innerHTML ?? '')

    // A browser leaves <br> or an empty paragraph behind when the last character goes.
    // Stored, that is a narrative the document would print a blank line for.
    return isRichTextEmpty(html) ? '' : html
  }

  useEffect(() => {
    // Chrome ends a line with a <div> and Firefox with a <br> unless this is set. Both are
    // handled on the way out, and asking for paragraphs means the markup stored is the
    // markup the document expects rather than something translated back into it.
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p')
    } catch {
      // Older browsers refuse the command. The sanitiser covers what they produce instead.
    }
  }, [])

  function run(command: string, value?: string) {
    const element = host.current
    if (!element || disabled) return

    // Focus first: a command applies to the selection, and clicking a toolbar control takes
    // the selection out of the box unless it is put back.
    element.focus()

    // Tags rather than styled spans: <b>, <font color>. They say the same thing in a
    // fraction of the characters, and the column this is stored in has a length. Asked for
    // with every command because the switch belongs to the document, not to this box - the
    // email editor in the Send dialog throws it the other way.
    try {
      document.execCommand('styleWithCSS', false, 'false')
    } catch {
      // An older browser refuses. The sanitiser and the renderer read both spellings.
    }

    document.execCommand(command, false, value)
    handleInput()
  }

  function link() {
    const element = host.current
    if (!element || disabled) return

    element.focus()
    const href = window.prompt('Link address', 'https://')

    // Cancelled, or emptied. Either way there is no link to make.
    if (!href || href.trim().length === 0) return

    document.execCommand('createLink', false, href.trim())
    handleInput()
  }

  function handleInput() {
    shown.current = host.current?.innerHTML ?? ''
    setEmpty(isRichTextEmpty(shown.current))
    onChange?.(read())
  }

  return (
    <div
      className={cx(
        'rounded-md border border-line bg-surface',
        'focus-within:border-accent',
        disabled && 'bg-sunken',
      )}
    >
      {!disabled && <EditorToolbar onCommand={run} onLink={link} />}

      <div className="relative">
        <div
          id={id}
          ref={host}
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={() => onBlur?.(read())}
          onPaste={(event) => {
            // Plain text, always. A paste from Word carries a stylesheet with it, and what
            // survives sanitising is the words anyway - taking them directly means the
            // paste looks the same on screen as it will on the document.
            event.preventDefault()
            const text = event.clipboardData.getData('text/plain')
            document.execCommand('insertText', false, text)
          }}
          style={{ minHeight }}
          className={cx(
            'prose-doc prose-print w-full px-3 py-2 text-sm text-ink focus:outline-none',
            disabled && 'text-muted',
          )}
        />

        {empty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-faint">
            {placeholder}
          </span>
        )}
      </div>
    </div>
  )
}
